import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { RefObject } from 'react';

import useOnClickLink from '../../hooks/useOnClickLink';
import useScrollTrigger from '../../hooks/useScrollTrigger';
import { useDocumentSurtitle } from '../../hooks/useSurtitle';
import { useTrackLink } from '../../hooks/useTracking';
import { createLocationsList } from '../../lib/blocks';
import getBlocksWithAddons, { adAddon, subscriptionAddon } from '../../lib/getBlocksWithAddons';
import { mergeRefs } from '../../lib/utils';

import { useIsCapture } from '../../contexts/LayoutContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import Ad from '../ads/Ad';
import HeaderAd from '../ads/HeaderAd';
import Blocks from '../blocks/Blocks';
import * as HeaderComponents from '../headers/document';
import CommentsList from '../lists/CommentsList';
import DocumentsList from '../lists/DocumentsList';
import DocumentMetadata from '../partials/DocumentMetadata';
import DocumentThemeComponent from '../themes/DocumentTheme';
import Body from '../typography/Body';
import ListTitle from '../typography/ListTitle';

import styles from '../../styles/documents/article-document.module.css';

interface ArticleDocumentProps extends Partial<ArticleLikeDocument> {
    // Ad space at the top of the page (`HeaderAd`), on the background of the page
    withHeaderAd?: boolean;
    commentableKey?: string | null;
    canComment?: boolean;
    placeholder?: boolean;
    isPreview?: boolean;
    className?: string | null;
    current?: boolean;
    entering?: boolean;
    transitioning?: boolean;
    withoutHeaderImage?: boolean;
    onProgress?: ((trigger: number) => void) | null;
    onComplete?: (() => void) | null;
}

function ArticleDocument({
    withHeaderAd = false,
    id = null,
    type = null,
    slug = null,
    title = null,
    subtitle = null,
    surtitle = null,
    image = null,
    images = null,
    metadata = null,
    theme = null,
    header = null,
    commentableKey = null,
    canComment = true,
    blocks = null,
    contest = null,
    form = null,
    placeholder = false,
    isPreview = false,
    current = false,
    transitioning = false,
    entering = false,
    className = null,
    withoutHeaderImage = false,
    onProgress = null,
    onComplete = null,
    ref = null,
}: ArticleDocumentProps & { ref?: RefObject<HTMLDivElement> | null }) {
    const { brands, categories, credits, authors, publishedAt, sponsors } = metadata || {};
    const { name: themeName = null, headerAnimation = null, withoutAds = false } = theme || {};
    const defaultBrand = useDefaultBrand();
    const { handle: defaultBrandHandle = 'urbania' } = defaultBrand || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle = defaultBrandHandle } = brand || {};

    const finalSurtitle = useDocumentSurtitle({
        type,
        surtitle,
        metadata,
    });

    const { header: headerImage = null } = images || {};

    const finalHeader = {
        id,
        role: 'header',
        type,
        title,
        surtitle: finalSurtitle,
        subtitle,
        categories,
        authors:
            authors || (credits || []).map(({ author = null }) => author).filter((a) => a !== null),
        date: type !== 'contest' ? publishedAt : null,
        image: headerImage || image,
        brand: brand || defaultBrand,
        sponsors,
        animation: headerAnimation,
        slug,
        ...header,
    };

    const { type: headerType = null } = (finalHeader || {}) as { type?: string | null };
    const HeaderComponent = getComponentFromName(HeaderComponents, headerType, 'default');

    const { locations = null } = metadata || {};
    const isCapture = useIsCapture();

    let addons = undefined;
    if (isCapture || withoutAds) {
        addons = [];
    } else if (contest !== null) {
        addons = [adAddon];
    } else if (isPreview) {
        addons = [adAddon, subscriptionAddon];
    }

    const blocksWithAddons = [
        ...getBlocksWithAddons(
            placeholder
                ? [
                      {
                          id: 'placeholder-1',
                          type: 'placeholder',
                      },
                      {
                          id: 'placeholder-2',
                          type: 'placeholder',
                      },
                  ]
                : blocks || [],
            {
                addons,
                addonsProps: {
                    ad: ({ count = 0 }: { count?: number }) => ({
                        // disabled: !current || placeholder,
                        slot: count === 0 ? 'article_native' : 'article',
                    }),
                    'form-campaign': {
                        documentId: id,
                    },
                },
            },
        ),
        ...(contest !== null
            ? [
                  {
                      id: 'contest',
                      type: 'contest',
                      contest,
                      form,
                  },
              ]
            : []),
        ...(locations !== null && locations.length > 0 ? [createLocationsList(locations)] : []),
    ];

    const onScrollTrigger = (trigger: number) => {
        if (onProgress !== null) {
            onProgress(trigger);
        }

        if (trigger === 1 && onComplete !== null) {
            onComplete();
        }
    };
    const { ref: scrollRef } = useScrollTrigger({
        disabled: !current || isPreview || transitioning || entering,
        onTrigger: onScrollTrigger,
    });

    const trackLink = useTrackLink();
    const onClickLink = ({
        url,
        external,
        label,
    }: {
        url: string;
        external: boolean;
        label?: string;
    }) => {
        if (external) {
            trackLink(url, {
                linkType: 'article',
                linkLabel: label || '',
            });
        }
    };
    const onClickBody = useOnClickLink(onClickLink);

    return (
        <DocumentThemeComponent
            id={`article-${id}`}
            {...theme}
            containerClassName={styles.container}
            className={classNames([styles.container, className])}
            data-brand={brandHandle}
            data-theme={themeName}
            ref={mergeRefs(ref, scrollRef)}
        >
            {withHeaderAd ? <HeaderAd /> : null}
            {finalHeader !== null ? (
                <HeaderComponent
                    {...finalHeader}
                    withoutImage={withoutHeaderImage}
                    placeholder={placeholder}
                    className={classNames([
                        styles.header,
                        brandHandle !== null ? styles[brandHandle] : null,
                    ])}
                />
            ) : null}

            <div className={styles.topAdContainer}>
                <Ad slot="top" alwaysRender className={styles.topAd} />
            </div>

            <Body className={classNames([styles.body, 'body-container'])} onClick={onClickBody}>
                <Blocks blocks={blocksWithAddons} />
            </Body>

            {metadata !== null ? (
                <DocumentMetadata {...metadata} className={styles.metadata} />
            ) : null}

            {canComment ? (
                <div className={styles.comments} id={current ? 'comments' : undefined}>
                    <CommentsList
                        commentableKey={commentableKey}
                        placeholder={placeholder}
                        disabled={!current || placeholder}
                        className={styles.commentsList}
                    />
                </div>
            ) : null}

            <div className={classNames([styles.readNext, 'article-document-read-next'])}>
                <ListTitle className={styles.title}>À consulter aussi</ListTitle>
                <DocumentsList
                    query={{
                        id,
                    }}
                    hook="next"
                    cardType="horizontal"
                    cardRatio="horizontal"
                    cardTheme="box-translucent"
                    cardWith={['brand-icon']}
                    count={3}
                    columns={1}
                    clickRef="read_next"
                    loading="lazy"
                    disabled={!current}
                />
            </div>
        </DocumentThemeComponent>
    );
}

export default ArticleDocument;
